import React, { useState, useEffect } from 'react';
import { callApi } from '../api/api';
import FirebaseImageUpload from '../components/FirebaseImageUpload';

const ProductModal = ({ product, onClose, onSave, showMessage }) => {
    const [formData, setFormData] = useState(product || {
        productName: '',
        company: '',
        mrp: '',
        unit: '',
        pack: '',
        category: '',
        imageUrl: '',
        prescription: false,
        inStock: true,
        stock: 0,
        saltComposition: '',
        packageVolume: '',
        description: ''
    });

    const [isGeneratingDesc, setIsGeneratingDesc] = useState(false);
    const [isAutoFilling, setIsAutoFilling] = useState(false);
    const [isScrapingImage, setIsScrapingImage] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAutoFill = async () => {
        if (!formData.productName) {
            showMessage('Please enter a Product Name first.', 'error');
            return;
        }

        setIsAutoFilling(true);
        try {
            const response = await callApi('/ai/auto-fill-product', 'POST', {
                productName: formData.productName
            });
            
            if (response.success && response.data) {
                setFormData(prev => ({ 
                    ...prev, 
                    ...response.data,
                    // keep original name just in case
                    productName: prev.productName 
                }));
                
                // Show the image description as a message if it provided one
                if (response.data.imageDescription) {
                    showMessage(`Auto-filled! Image hint: ${response.data.imageDescription}`, 'success');
                } else {
                    showMessage('Product details auto-filled successfully!', 'success');
                }
            } else {
                showMessage(response.message || 'Failed to auto-fill details.', 'error');
            }
        } catch (error) {
            showMessage(error.message || 'Failed to auto-fill details.', 'error');
        } finally {
            setIsAutoFilling(false);
        }
    };

    const handleGenerateDescription = async () => {
        if (!formData.productName) {
            showMessage('Product Name is required to generate a description.', 'error');
            return;
        }

        setIsGeneratingDesc(true);
        try {
            const response = await callApi('/ai/generate-description', 'POST', {
                productName: formData.productName,
                company: formData.company,
                category: formData.category,
                saltComposition: formData.saltComposition
            });
            
            if (response.success && response.description) {
                setFormData(prev => ({ ...prev, description: response.description }));
                showMessage('Description generated successfully!', 'success');
            } else {
                showMessage(response.message || 'Failed to generate description.', 'error');
            }
        } catch (error) {
            showMessage(error.message || 'Failed to generate description.', 'error');
        } finally {
            setIsGeneratingDesc(false);
        }
    };

    const handleScrapeImage = async () => {
        if (!formData.productName) {
            showMessage('Product Name is required to find an image.', 'error');
            return;
        }

        setIsScrapingImage(true);
        try {
            const response = await callApi('/admin/scrape-image', 'POST', {
                productName: formData.productName
            });
            
            if (response.success && response.imageUrl) {
                setFormData(prev => ({ ...prev, imageUrl: response.imageUrl }));
                showMessage('Image found and applied successfully!', 'success');
            } else {
                showMessage(response.message || 'No image found for this product.', 'error');
            }
        } catch (error) {
            showMessage(error.message || 'Failed to scrape image. Make sure the backend endpoint exists.', 'error');
        } finally {
            setIsScrapingImage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataToSubmit = { ...formData };
            if (dataToSubmit.imageUrl) {
                dataToSubmit.hasRealImage = true;
            }

            const endpoint = formData._id ? `/api/admin/products/${formData._id}` : '/api/admin/products';
            const method = formData._id ? 'PUT' : 'POST';
            const savedProduct = await callApi(endpoint, method, dataToSubmit);
            onSave(savedProduct);
            showMessage(`Product ${formData._id ? 'updated' : 'created'} successfully!`, 'success');
            onClose();
        } catch (error) {
            showMessage(error.message || 'Failed to save product.', 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-2 sm:p-4 animate-fade-in">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden border border-slate-100">
                <div className="bg-slate-50 px-4 sm:px-8 py-4 sm:py-6 border-b border-slate-100 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="text-lg sm:text-xl font-extrabold text-slate-800">{formData._id ? 'Edit Product' : 'Add New Product'}</h3>
                        <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">{formData._id ? 'Update inventory details below.' : 'Create a new listing in the inventory.'}</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-slate-400 hover:text-slate-600 shadow-sm border border-slate-200 transition-colors">
                        <i className="fas fa-times text-xs"></i>
                    </button>
                </div>

                <div className="overflow-y-auto p-4 sm:p-6 md:p-8 custom-scrollbar">
                    <form id="productForm" onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        
                        {/* Column 1: Basic Details */}
                        <div className="space-y-5">
                            <div>
                                <div className="mb-1.5 ml-1 mr-1">
                                    <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400">Product Name *</label>
                                </div>
                                <input type="text" name="productName" value={formData.productName || formData.Product || ''} onChange={handleChange} required 
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-semibold text-slate-800 outline-none transition-all" />
                            </div>
                            
                            <div>
                                <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 ml-1">Manufacturer / Brand</label>
                                <input type="text" name="company" value={formData.company || formData.Company || ''} onChange={handleChange} 
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-semibold text-slate-800 outline-none transition-all" />
                            </div>

                            <div className="grid grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 ml-1">MRP (₹)</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-slate-400 font-bold">₹</span>
                                        </div>
                                        <input type="number" name="mrp" value={formData.mrp || formData.MRP || ''} onChange={handleChange} 
                                            className="w-full pl-8 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-bold text-sky-700 outline-none transition-all" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 ml-1">Category</label>
                                    <input type="text" name="category" value={formData.category || formData.Category || ''} onChange={handleChange} placeholder="e.g. Skin Care" 
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-semibold text-slate-800 outline-none transition-all" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 ml-1">Unit</label>
                                    <input type="text" name="unit" value={formData.unit || formData.Unit || ''} onChange={handleChange} placeholder="e.g. 10GM" 
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-semibold text-slate-800 outline-none transition-all" />
                                </div>
                                <div>
                                    <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 ml-1">Pack</label>
                                    <input type="text" name="pack" value={formData.pack || formData.Pack || ''} onChange={handleChange} placeholder="e.g. 1" 
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-semibold text-slate-800 outline-none transition-all" />
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Extended Details & Settings */}
                        <div className="space-y-5">
                            <div>
                                <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 ml-1">Package Volume</label>
                                <input type="text" name="packageVolume" value={formData.packageVolume || ''} onChange={handleChange} placeholder="e.g. 100ml, 20x10x5 cm" 
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-semibold text-slate-800 outline-none transition-all" />
                            </div>

                            <div>
                                <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 ml-1">Salt Composition</label>
                                <textarea name="saltComposition" value={formData.saltComposition || ''} onChange={handleChange} rows="2"
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-semibold text-slate-800 outline-none transition-all resize-none"></textarea>
                            </div>

                            <div>
                                <div className="mb-1.5 ml-1 mr-1">
                                    <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400">Description</label>
                                </div>
                                <textarea name="description" value={formData.description || ''} onChange={handleChange} rows="4" placeholder="Product description..."
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-semibold text-slate-800 outline-none transition-all resize-none"></textarea>
                            </div>

                            <div>
                                <div className="mb-1.5 ml-1 mr-1">
                                    <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400">Product Image</label>
                                </div>
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                    <input type="text" name="imageUrl" value={formData.imageUrl || ''} onChange={handleChange} placeholder="Paste image URL here" 
                                        className="w-full p-2 mb-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-medium text-slate-600 outline-none transition-all" />
                                    
                                    <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0">
                                            {formData.imageUrl ? (
                                                <img src={formData.imageUrl} alt="Preview" className="w-12 h-12 rounded-lg object-contain border border-slate-200 bg-white" />
                                            ) : (
                                                <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
                                                    <i className="fas fa-image"></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-grow">
                                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Or Upload File</p>
                                            <FirebaseImageUpload onUploadSuccess={(url) => setFormData({ ...formData, imageUrl: url })} folder="products" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5 ml-1">Stock Quantity</label>
                                <input type="number" name="stock" value={formData.stock !== undefined ? formData.stock : (formData.balanceQuantity || 0)} onChange={handleChange} 
                                    className="w-1/2 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm font-bold text-slate-800 outline-none transition-all" />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 pt-2">
                                <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.prescription ? 'border-amber-400 bg-amber-50' : 'border-slate-100 bg-white hover:border-slate-200'}`}>
                                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${formData.prescription ? 'bg-amber-500 text-white' : 'bg-slate-200'}`}>
                                        {formData.prescription && <i className="fas fa-check text-[10px]"></i>}
                                    </div>
                                    <input type="checkbox" name="prescription" checked={formData.prescription || false} onChange={handleChange} className="hidden" />
                                    <div>
                                        <p className="font-bold text-slate-700 text-sm">Rx Required</p>
                                        <p className="text-[10px] text-slate-500">Needs prescription</p>
                                    </div>
                                </label>

                                <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.inStock ? 'border-emerald-400 bg-emerald-50' : 'border-slate-100 bg-white hover:border-slate-200'}`}>
                                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${formData.inStock ? 'bg-emerald-500 text-white' : 'bg-slate-200'}`}>
                                        {formData.inStock && <i className="fas fa-check text-[10px]"></i>}
                                    </div>
                                    <input type="checkbox" name="inStock" checked={formData.inStock || false} onChange={handleChange} className="hidden" />
                                    <div>
                                        <p className="font-bold text-slate-700 text-sm">In Stock</p>
                                        <p className="text-[10px] text-slate-500">Available for sale</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="bg-slate-50 px-8 py-5 border-t border-slate-100 flex justify-end gap-3 shrink-0">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-xl border border-slate-200 font-bold text-slate-600 hover:bg-white transition-colors">Cancel</button>
                    <button type="submit" form="productForm" className="px-8 py-2.5 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-extrabold shadow-lg shadow-sky-500/20 transition-all transform hover:-translate-y-0.5">
                        <i className="fas fa-save mr-2"></i> {formData._id ? 'Save Changes' : 'Create Product'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const BulkUploadModal = ({ onClose, onUploadSuccess, showMessage }) => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
                setFile(selectedFile);
            } else {
                showMessage('Please select a valid CSV file.', 'error');
                e.target.value = '';
            }
        }
    };

    const handleUpload = async () => {
        if (!file) {
            showMessage('Please select a file first.', 'error');
            return;
        }

        setIsUploading(true);
        const reader = new FileReader();
        
        reader.onload = async (e) => {
            const csvText = e.target.result;
            try {
                const token = localStorage.getItem('userToken');
                let API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
                if (!!(window.Capacitor && window.Capacitor.isNative)) {
                    API_BASE_URL = 'https://pharma-retail-online.web.app/api';
                }

                const response = await fetch(`${API_BASE_URL}/admin/products/bulk-upload`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({ csvData: csvText })
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Failed to upload CSV');
                }

                showMessage(`Upload complete! Inserted: ${data.inserted}, Updated: ${data.updated}`, 'success');
                onUploadSuccess();
                onClose();
            } catch (error) {
                showMessage(error.message || 'An error occurred during upload', 'error');
            } finally {
                setIsUploading(false);
            }
        };

        reader.onerror = () => {
            showMessage('Failed to read the file.', 'error');
            setIsUploading(false);
        };

        reader.readAsText(file);
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
                <div className="bg-slate-50 px-6 py-5 border-b border-slate-100 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="text-xl font-extrabold text-slate-800">Bulk Upload Products</h3>
                        <p className="text-sm text-slate-500 font-medium mt-1">Upload a CSV file to add/update multiple products.</p>
                    </div>
                    <button onClick={onClose} disabled={isUploading} className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-slate-400 hover:text-slate-600 shadow-sm border border-slate-200 transition-colors disabled:opacity-50">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="p-6">
                    <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 mb-6">
                        <h4 className="font-bold text-sky-800 text-sm mb-2"><i className="fas fa-info-circle mr-1"></i> Expected CSV Format</h4>
                        <p className="text-xs text-sky-700 leading-relaxed">
                            Ensure your CSV has a header row. The primary identifier is <span className="font-bold">Product Name</span>.
                            Other supported columns: Brand, Price, Description, Category, Stock, Prescription (Yes/No), Salt Composition.
                        </p>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-bold text-slate-700 mb-2">Select CSV File</label>
                        <input 
                            type="file" 
                            accept=".csv,text/csv" 
                            onChange={handleFileChange}
                            disabled={isUploading}
                            className="block w-full text-sm text-slate-500
                                file:mr-4 file:py-2.5 file:px-4
                                file:rounded-xl file:border-0
                                file:text-sm file:font-semibold
                                file:bg-sky-50 file:text-sky-700
                                hover:file:bg-sky-100 transition-all
                                border border-slate-200 rounded-xl p-1 bg-slate-50"
                        />
                    </div>

                    {file && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 font-medium bg-slate-50 p-3 rounded-xl border border-slate-200">
                            <i className="fas fa-file-csv text-emerald-500 text-xl"></i>
                            <span className="truncate">{file.name}</span>
                            <span className="text-xs text-slate-400 ml-auto">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                    )}
                </div>

                <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end gap-3 shrink-0">
                    <button type="button" onClick={onClose} disabled={isUploading} className="px-5 py-2.5 rounded-xl border border-slate-200 font-bold text-slate-600 hover:bg-white transition-colors disabled:opacity-50">Cancel</button>
                    <button 
                        onClick={handleUpload} 
                        disabled={!file || isUploading} 
                        className="px-6 py-2.5 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-extrabold shadow-lg shadow-sky-500/20 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 disabled:shadow-none flex items-center gap-2"
                    >
                        {isUploading ? (
                            <><i className="fas fa-circle-notch fa-spin"></i> Uploading...</>
                        ) : (
                            <><i className="fas fa-upload"></i> Start Upload</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminProducts = ({ showMessage }) => {
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const fetchProducts = async () => {
        setIsLoading(true);
        try {
            const data = await callApi('/admin/products');
            const productsList = data.products || data;
            if (productsList && Array.isArray(productsList)) {
                productsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            }
            setProducts(productsList);
        } catch (error) {
            console.error("Failed to fetch products", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleEdit = (product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        try {
            await callApi(`/api/admin/products/${id}`, 'DELETE');
            setProducts(products.filter(p => p._id !== id));
            showMessage('Product deleted successfully.', 'success');
        } catch (error) {
            showMessage('Failed to delete product.', 'error');
        }
    };

    const filteredProducts = products.filter(p => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const name = (p.productName || p.Product || p.name || '').toLowerCase();
        const brand = (p.company || p.Company || p.brand || '').toLowerCase();
        return name.includes(query) || brand.includes(query);
    });

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                <i className="fas fa-circle-notch fa-spin text-4xl text-sky-500 mb-4"></i>
                <p className="text-slate-400 font-medium">Loading inventory...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-2">
                <div>
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800">Inventory Management</h2>
                    <p className="text-xs text-slate-500 font-medium mt-0.5">{products.length} products listed</p>
                </div>
                
                <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2.5">
                    <div className="relative w-full sm:w-60">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                            <i className="fas fa-search text-xs"></i>
                        </div>
                        <input 
                            type="text" 
                            placeholder="Search products..." 
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-all shadow-sm"
                        />
                    </div>
                    
                    <div className="flex gap-2 w-full sm:w-auto">
                        <button 
                            onClick={() => setIsBulkModalOpen(true)}
                            className="flex-1 sm:flex-initial bg-emerald-500 hover:bg-emerald-600 text-white px-3.5 py-2 rounded-xl font-bold transition-all shadow-sm flex items-center justify-center gap-1.5 text-xs whitespace-nowrap"
                        >
                            <i className="fas fa-file-csv"></i> <span>Bulk Upload</span>
                        </button>
                        
                        <button 
                            onClick={() => { setSelectedProduct(null); setIsModalOpen(true); }}
                            className="flex-1 sm:flex-initial bg-sky-500 hover:bg-sky-600 text-white px-3.5 py-2 rounded-xl font-bold transition-all shadow-sm flex items-center justify-center gap-1.5 text-xs whitespace-nowrap"
                        >
                            <i className="fas fa-plus"></i> <span>Add Product</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm flex-grow flex flex-col overflow-hidden min-h-[400px]">
                <div className="overflow-x-auto flex-grow">
                    <table className="w-full text-left border-collapse min-w-[640px]">
                        <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
                            <tr>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Product Details</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Category</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">MRP</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Inventory</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(product => {
                                const pName = product.productName || product.Product || product.name;
                                const pCompany = product.company || product.Company || product.brand;
                                const pMRP = product.mrp || product.MRP || product.price;
                                const pStock = product.stock !== undefined ? product.stock : (product.balanceQuantity !== undefined ? product.balanceQuantity : 0);
                                const pImage = product.imageUrl || product.image;

                                return (
                                    <tr key={product._id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 overflow-hidden flex-shrink-0 flex items-center justify-center p-1">
                                                    {pImage ? (
                                                        <img src={pImage} alt={pName} className="w-full h-full object-contain" onError={(e) => { e.target.src = 'https://placehold.co/400x300/e0f2f7/007bff?text=Product'; }} />
                                                    ) : (
                                                        <i className="fas fa-pills text-slate-300 text-xl"></i>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-800 line-clamp-1">{pName}</p>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="text-xs text-slate-500 font-medium">{pCompany || 'Unknown Brand'}</span>
                                                        {product.prescription && (
                                                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-extrabold tracking-wider uppercase">Rx</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-600">
                                                {product.category || product.Category || 'Uncategorized'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="font-black text-slate-700">₹{pMRP}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            {product.inStock ? (
                                                <div className="flex flex-col">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 w-fit">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> In Stock
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 font-medium mt-1 ml-1">{pStock} units available</span>
                                                </div>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-red-50 text-red-700 w-fit">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Out of Stock
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => handleEdit(product)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-sky-600 hover:bg-sky-50 hover:border-sky-200 transition-all flex items-center justify-center shadow-sm" title="Edit">
                                                    <i className="fas fa-pen text-xs"></i>
                                                </button>
                                                <button onClick={() => handleDelete(product._id)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-red-500 hover:bg-red-50 hover:border-red-200 transition-all flex items-center justify-center shadow-sm" title="Delete">
                                                    <i className="fas fa-trash-alt text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredProducts.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center">
                                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <i className="fas fa-search text-2xl text-slate-300"></i>
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-700 mb-1">No products found</h3>
                                        <p className="text-sm text-slate-500">Try adjusting your search query.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {Math.ceil(filteredProducts.length / itemsPerPage) > 1 && (
                    <div className="flex justify-between items-center p-4 border-t border-slate-100 bg-slate-50">
                        <button 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                            disabled={currentPage === 1}
                            className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        >
                            Previous
                        </button>
                        <span className="text-xs font-bold text-slate-500">
                            Page {currentPage} of {Math.ceil(filteredProducts.length / itemsPerPage)}
                        </span>
                        <button 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredProducts.length / itemsPerPage)))} 
                            disabled={currentPage === Math.ceil(filteredProducts.length / itemsPerPage)}
                            className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <ProductModal 
                    product={selectedProduct} 
                    onClose={() => setIsModalOpen(false)} 
                    onSave={() => { fetchProducts(); }} 
                    showMessage={showMessage} 
                />
            )}

            {isBulkModalOpen && (
                <BulkUploadModal
                    onClose={() => setIsBulkModalOpen(false)}
                    onUploadSuccess={() => { fetchProducts(); }}
                    showMessage={showMessage}
                />
            )}
        </div>
    );
};

export default AdminProducts;
